import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export interface DropdownItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  divider?: boolean;
  children?: DropdownItem[];
  onClick?: () => void;
}

export interface DropdownProps {
  trigger: React.ReactElement;
  items: DropdownItem[];
  align?: 'left' | 'right';
  className?: string;
}

/* ─── Dropdown ──────────────────────────────────────────────── */
export function Dropdown({ trigger, items, align = 'left', className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const toggleOpen = useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: align === 'right' ? rect.right + window.scrollX : rect.left + window.scrollX,
      });
    }
    setOpen((v) => !v);
  }, [open, align]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Keep dropdown inside viewport (left/right/top/bottom).
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const pad = 8;
    const rect = menuRef.current.getBoundingClientRect();
    let nextLeft = coords.left;
    let nextTop = coords.top;
    if (rect.right > window.innerWidth - pad) {
      nextLeft -= rect.right - (window.innerWidth - pad);
    }
    if (rect.left < pad) {
      nextLeft += pad - rect.left;
    }
    if (rect.bottom > window.innerHeight - pad) {
      nextTop -= rect.bottom - (window.innerHeight - pad);
    }
    if (rect.top < pad) {
      nextTop += pad - rect.top;
    }
    if (Math.abs(nextLeft - coords.left) > 0.5 || Math.abs(nextTop - coords.top) > 0.5) {
      setCoords({ top: nextTop, left: nextLeft });
    }
  }, [open, coords.left, coords.top]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open || !menuRef.current) return;
      const focusable = Array.from(
        menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')
      );
      const idx = focusable.indexOf(document.activeElement as HTMLElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusable[(idx + 1) % focusable.length]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusable[(idx - 1 + focusable.length) % focusable.length]?.focus();
      }
    },
    [open]
  );

  return (
    <>
      <div ref={triggerRef} className="inline-flex">
        {React.cloneElement(trigger, {
          onClick: toggleOpen,
          'aria-haspopup': 'menu',
          'aria-expanded': open,
          'aria-controls': open ? menuId : undefined,
        } as React.HTMLAttributes<HTMLElement>)}
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={menuRef}
              id={menuId}
              role="menu"
              aria-orientation="vertical"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.12 }}
              style={{
                position: 'absolute',
                top: coords.top,
                left: coords.left,
                ...(align === 'right' ? { transform: 'translateX(-100%)' } : {}),
              }}
              onKeyDown={handleKeyDown}
              className={cn(
                'z-[9998] min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg',
                'dark:border-gray-700 dark:bg-gray-900',
                className
              )}
            >
              <DropdownMenu items={items} onClose={() => setOpen(false)} />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

/* ─── Menu Items (recursive for submenus) ───────────────────── */
function DropdownMenu({ items, onClose }: { items: DropdownItem[]; onClose: () => void }) {
  return (
    <>
      {items.map((item) => {
        if (item.divider) {
          return (
            <div
              key={item.key}
              role="separator"
              className="my-1 h-px bg-gray-200 dark:bg-gray-700"
            />
          );
        }

        if (item.children?.length) {
          return <DropdownSubmenu key={item.key} item={item} onClose={onClose} />;
        }

        return (
          <button
            key={item.key}
            role="menuitem"
            type="button"
            disabled={item.disabled}
            aria-disabled={item.disabled}
            onClick={() => {
              item.onClick?.();
              onClose();
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-gray-800',
              item.disabled && 'pointer-events-none opacity-50',
              item.danger
                ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            )}
          >
            {item.icon && <span className="shrink-0" aria-hidden="true">{item.icon}</span>}
            {item.label}
          </button>
        );
      })}
    </>
  );
}

function DropdownSubmenu({ item, onClose }: { item: DropdownItem; onClose: () => void }) {
  const [open, setOpen] = useState(false);
  const submenuRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
      requestAnimationFrame(() => {
        const first = submenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
        first?.focus();
      });
    } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        role="menuitem"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex w-full items-center justify-between gap-2 px-3 py-2 text-sm text-gray-700',
          'hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
          'focus-visible:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-gray-800'
        )}
      >
        <span className="flex items-center gap-2">
          {item.icon && <span className="shrink-0" aria-hidden="true">{item.icon}</span>}
          {item.label}
        </span>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
      </button>

      <AnimatePresence>
        {open && item.children && (
          <motion.div
            ref={submenuRef}
            role="menu"
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={{ duration: 0.1 }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'ArrowLeft' || e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              }
            }}
            className={cn(
              'absolute start-full top-0 z-10 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg',
              'dark:border-gray-700 dark:bg-gray-900'
            )}
          >
            <DropdownMenu items={item.children} onClose={onClose} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

Dropdown.displayName = 'Dropdown';
